import { wrapRequest } from '@applandeo/api-wrapper';
import { BaseSyntheticEvent, ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ALERT_VARIANT, useAlertMessages } from '@/components/Global/AlertMessages/AlertMessagesProvider';
import { getAbsenceTypes } from '@/dataExchange/services/myOrganization.service';
import {
    acceptRequestDeletion,
    getAllRequests,
    rejectRequestDeletion,
    updateAbsenceStatus,
} from '@/dataExchange/services/requestsPage.service';
import { ApiResponseWithPagination, Pagination } from '@/domain/common';
import { AbsenceTypeResponseDto } from '@/domain/myOrganization';
import { NewRequestWhere } from '@/domain/newRequest';
import {
    AbsenceResponseDto,
    AbsenceWithTypeData,
    AllRequestsParamsDto,
    DateRange,
    RequestsListFilters,
} from '@/domain/requestsPage';
import useModal from '@/hooks/useModal/useModal';
import useTrackAbsenceApproval from '@/hooks/useTrackAbsenceApproval/useTrackAbsenceApproval';
import useLoggedInUserData from '@/hooks/useUserData/useLoggedInUserData';
import { RangeCalendarDate } from '@/pages/Calendar/types';
import { useSentryActions } from '@/sentry/hooks/useSentryActions';
import { SENTRY_ACTIONS } from '@/sentry/sentryActions';
import { CALENDAR_REQUESTS_PARAMS, FIRST_PAGE, INITIAL_PAGINATION } from '@/utils/constants/common';
import { normalizeDateForAPI } from '@/utils/date/date';
import { ORDER_BY } from '@/utils/enums/common';
import { checkIsCustomPeriod, REQUEST_STATUS, REQUESTS_PERIOD_DATE } from '@/utils/enums/requestsPage';

const prepareParams = (
    { page, pageSize }: Pagination,
    filters?: RequestsListFilters
): AllRequestsParamsDto | undefined => {
    const { period, status, order, customPeriodDates } = filters ?? {};
    const params = { status, order, page, pageSize, orderBy: ORDER_BY.FROM };
    const isCustomPeriod = checkIsCustomPeriod(period);

    if (isCustomPeriod) {
        if (!customPeriodDates?.startDate || !customPeriodDates?.endDate) return;

        const formattedDates: DateRange = {
            startDate: normalizeDateForAPI(customPeriodDates.startDate),
            endDate: normalizeDateForAPI(customPeriodDates.endDate),
        };
        return { ...params, ...formattedDates };
    }

    const { startDate, endDate } = (period && REQUESTS_PERIOD_DATE[period]) || {};

    if (!startDate || !endDate) return params;

    return { ...params, startDate: normalizeDateForAPI(startDate), endDate: normalizeDateForAPI(endDate) };
};

interface UseLeadRequestsTableLogic {
    isAcceptTab?: boolean;
    isCalendar?: boolean;
    rangeDates?: RangeCalendarDate;
    onUpdateSuccessCallback?: () => void;
}

const useLeadRequestsTableLogic = ({
    isAcceptTab,
    isCalendar,
    rangeDates,
    onUpdateSuccessCallback,
}: UseLeadRequestsTableLogic) => {
    const { t } = useTranslation();

    const getAllRequestsWrapper = wrapRequest(getAllRequests);
    const getAllRequestsTypeWrapper = wrapRequest(getAbsenceTypes);
    const updateRequestStatusWrapper = wrapRequest(updateAbsenceStatus);
    const acceptRequestDeletionWrapper = wrapRequest(acceptRequestDeletion);
    const rejectRequestDeletionWrapper = wrapRequest(rejectRequestDeletion);

    const { createAlert } = useAlertMessages();
    const { credentialId } = useLoggedInUserData();
    const { handleSentryAction } = useSentryActions();
    const { trackApproval } = useTrackAbsenceApproval();

    const [isLoading, setIsLoading] = useState(true);
    const [requestsList, setRequestsList] = useState<ApiResponseWithPagination<AbsenceWithTypeData[]>>();
    const [requestsTypes, setRequestsTypes] = useState<AbsenceTypeResponseDto[]>();

    const [paginationData, setPaginationData] = useState<Pagination>(INITIAL_PAGINATION);
    const [filters, setFilters] = useState<RequestsListFilters>();

    const { isModalOpen, openModal, onCloseModal } = useModal();
    const [singleRequestDetails, setSingleRequestDetails] = useState<AbsenceWithTypeData | null>(null);
    const { isLead, isAdmin } = useLoggedInUserData();

    const calendarParams = useMemo(() => ({ ...CALENDAR_REQUESTS_PARAMS, ...rangeDates }), [rangeDates]);

    const mergedRequestsWithTypes = useCallback(
        (requests: ApiResponseWithPagination<AbsenceResponseDto[]>, types: AbsenceTypeResponseDto[]) => {
            const mergedRequests: AbsenceWithTypeData[] = requests?.elements?.map(request => {
                const absenceType = types?.find(({ _id }) => _id === request?.absenceTypeId);

                return {
                    ...request,
                    typeData: absenceType,
                };
            });

            setRequestsList({ ...requests, elements: mergedRequests });
            setIsLoading(false);
        },
        []
    );

    const getRequestsTypes = useCallback(async () => {
        return getAllRequestsTypeWrapper
            .fetch(true)
            .onSuccess((types: AbsenceTypeResponseDto[]) => setRequestsTypes(types))
            .onAnyError(error => {
                createAlert(ALERT_VARIANT.error, t('requests_alerts_fail_get_requests'));
                handleSentryAction(error, SENTRY_ACTIONS.ERROR_GET_ALL_REQUESTS_TYPE);
                setIsLoading(false);
            })
            .concludeWithPromise();
    }, [createAlert, getAllRequestsTypeWrapper, t]);

    const getRequests = useCallback(async () => {
        const params = prepareParams(paginationData, filters);
        if (!params) return;

        setIsLoading(true);

        const types = requestsTypes || (await getRequestsTypes());

        if (!types) return;

        const absenceTypeId =
            filters?.absenceType && typeof filters.absenceType === 'object' ? filters.absenceType._id : undefined;

        getAllRequestsWrapper
            .fetch({
                userId: filters?.onlyOwn || (!isAdmin && !isLead) ? credentialId : undefined,
                /**
                 * For Admin to omit api restrictions and get all of users absences:
                 * filterManagerId should be empty
                 * filterManagerInclusive should be falsy
                 */
                filterManagerId: isLead ? credentialId : undefined,
                filterManagerInclusive: isCalendar ? !isAdmin && !isLead : !isAdmin,
                requestListType: isAcceptTab ? 'toAccept' : undefined,
                absenceTypeId,
                ...(isCalendar ? calendarParams : params),
            })
            .onSuccess(requests => {
                mergedRequestsWithTypes(requests, types);
            })
            .onAnyError(error => {
                createAlert(ALERT_VARIANT.error, t('requests_alerts_fail_get_requests'));
                handleSentryAction(error, SENTRY_ACTIONS.ERROR_GET_ALL_REQUESTS);
                setIsLoading(false);
            });
    }, [
        filters,
        paginationData,
        requestsTypes,
        getRequestsTypes,
        getAllRequestsWrapper,
        credentialId,
        mergedRequestsWithTypes,
        createAlert,
        t,
        calendarParams,
    ]);

    useEffect(() => {
        const calendarWithNoRangeDates = isCalendar && !calendarParams.startDate && !calendarParams.endDate;

        if ((!paginationData || !filters) && !isCalendar) return;
        if (calendarWithNoRangeDates) return;

        getRequests();
    }, [filters, paginationData, calendarParams, isCalendar]);

    const onFiltersChange = useCallback((filters: RequestsListFilters) => {
        setFilters(filters);
    }, []);

    const onPageChange = useCallback(
        (_, newPage: number) => {
            const pageForApi = newPage + 1;
            setPaginationData({ ...paginationData, page: pageForApi });
        },
        [paginationData]
    );

    const onRowsPerPageChange = useCallback(
        (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            setPaginationData({ ...paginationData, page: FIRST_PAGE, pageSize: parseInt(event?.target?.value, 10) });
        },
        [paginationData]
    );

    const openDetailsModal = useCallback(
        (requestDetails: AbsenceWithTypeData) => {
            setSingleRequestDetails(requestDetails);
            openModal();
        },
        [openModal]
    );

    const onCloseDetailsModal = useCallback(() => {
        setSingleRequestDetails(null);
        onCloseModal();
    }, [onCloseModal]);

    const onUpdateAbsenceStatus = useCallback(
        (absenceId, status, name, isDeletionRequest, where?: NewRequestWhere) => (event: BaseSyntheticEvent) => {
            event.stopPropagation();

            (isDeletionRequest
                ? status === REQUEST_STATUS.ACCEPTED
                    ? acceptRequestDeletionWrapper.fetch(absenceId)
                    : rejectRequestDeletionWrapper.fetch(absenceId)
                : updateRequestStatusWrapper.fetch({ absenceId, status })
            )
                .onAnyError(error => {
                    createAlert(
                        ALERT_VARIANT.error,
                        status === REQUEST_STATUS.ACCEPTED
                            ? t(`requests_alerts_accepted_failed`)
                            : t(`requests_alerts_reject_failed`)
                    );
                    handleSentryAction(error, SENTRY_ACTIONS.ERROR_UPDATE_ABSENCE_STATUS);
                })
                .onSuccess(() => {
                    createAlert(
                        ALERT_VARIANT.success,
                        status === REQUEST_STATUS.ACCEPTED
                            ? t(`requests_alerts_accepted_success`)
                            : t(`applicationsToAccept_applicationRejected_message`)
                    );

                    onCloseDetailsModal();
                    setPaginationData(INITIAL_PAGINATION);
                    getRequests();

                    if (onUpdateSuccessCallback) {
                        onUpdateSuccessCallback();
                    }

                    trackApproval({ isDeletionRequest, absenceName: name, status, where });
                });
        },
        [createAlert, getRequests, onCloseDetailsModal, t, updateRequestStatusWrapper, onUpdateSuccessCallback]
    );

    return {
        requestsList,
        isLoading: isLoading || updateRequestStatusWrapper.isLoading,
        onFiltersChange,
        paginationData,
        onPageChange,
        onRowsPerPageChange,
        singleRequestDetails,
        isModalOpen,
        onCloseDetailsModal,
        openDetailsModal,
        onUpdateAbsenceStatus,
        getRequests,
    };
};

export default useLeadRequestsTableLogic;
